<template>
  <v-tabs
    :model-value="modelValue"
    :density="density"
    :aria-label="ariaLabel"
    slider-color="primary"
    class="ll-tabs"
    @update:model-value="emit('update:modelValue', $event as TabValue)"
  >
    <v-tab
      v-for="item in items"
      :key="String(item.value)"
      :value="item.value"
      :prepend-icon="item.icon"
      :disabled="item.disabled === true"
      :data-cy="item.dataCy"
      @click="emit('select', item.value)"
    >
      <slot name="label" :item="item">{{ item.label }}</slot>
    </v-tab>
  </v-tabs>
</template>

<script setup lang="ts">
/**
 * A row of tabs: the house "current page" treatment for Vuetify tabs. The selected tab
 * gets a primary slider, an 8% primary tint and medium weight (the tint is on
 * `.ll-tabs` in style.css), and its label stays in the normal text colour.
 *
 * There is deliberately no `color` prop: Vuetify paints the selected label in `color`,
 * which is the green-text active state this replaced. Tabs come from `items` so every
 * tab gets the same treatment; a label that needs markup (a count, say) uses the
 * `label` slot. Hide a tab by leaving it out of `items`.
 *
 * `select` fires on every click, including on the current tab. Tabs that navigate need
 * it: re-clicking the current tab can still mean "back to this tab's top level".
 */
export type TabValue = string | number;

export interface TabItem {
  value: TabValue;
  label: string;
  icon?: string;
  disabled?: boolean;
  dataCy?: string;
}

withDefaults(
  defineProps<{
    modelValue: TabValue | null | undefined;
    items: TabItem[];
    /** Names the tab list for screen readers when nothing nearby does. */
    ariaLabel?: string;
    density?: 'default' | 'comfortable' | 'compact';
  }>(),
  {
    ariaLabel: undefined,
    density: 'compact',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: TabValue];
  select: [value: TabValue];
}>();
</script>
